import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import Layout from '../Layout/Layout';
import Hero from '../components/HomePage/Hero';
import ExchangeBanner from '../components/HomePage/ExchangeBanner';
import DailyBlogPost from '../components/HomePage/DailyBlogPost';
import WhyReasons from '../components/HomePage/WhyReasons';
import { AppButtonLink } from '../components/AppButton/AppButton';
import '../components/HomePage/HomePage.css';

const BOT_TYPES = [
    {
        icon: 'mdi:brain',
        title: 'Algo Bot',
        description: 'Build full strategies with monitors, conditions, and order templates.',
        link: '/learn/algo-bot',
    },
    {
        icon: 'mdi:clock-outline',
        title: 'Scheduled Bot',
        description: 'Automate DCA and time-based entries on a cron or fixed schedule.',
        link: '/learn/scheduled-bot',
    },
    {
        icon: 'mdi:grid',
        title: 'Grid Bot',
        description: 'Profit from range-bound markets with layered buy and sell orders.',
        link: '/learn/grid-bot',
    },
];

const PLATFORM_FEATURES = [
    {
        icon: 'mdi:shield-check-outline',
        title: 'Secure by design',
        description: 'Trade-only API keys. Your funds stay on your exchange.',
    },
    {
        icon: 'mdi:chart-multiple',
        title: 'Real-time dashboard',
        description: 'Wallets, orders, tickers, and bot status in one view.',
    },
    {
        icon: 'mdi:file-document-outline',
        title: 'Setup guides',
        description: 'Step-by-step docs for every bot type in the Learn section.',
    },
    {
        icon: 'mdi:layers-outline',
        title: 'Transparent limits',
        description: 'Plan-based caps on bots, monitors, and monthly backtests.',
        link: '/pricing',
    },
];

function FeatureCard({ item }) {
    const content = (
        <>
            <span className="home-feature-icon" aria-hidden="true">
                <Icon icon={item.icon} width={22} height={22} />
            </span>
            <h3 className="home-feature-title">{item.title}</h3>
            <p className="home-feature-text">{item.description}</p>
            {item.link && <span className="home-feature-link">Learn more →</span>}
        </>
    );

    if (item.link) {
        return (
            <Link to={item.link} className="home-feature-card">
                {content}
            </Link>
        );
    }

    return <article className="home-feature-card">{content}</article>;
}

const Home = () => {
    return (
        <Layout>
            <div className="home-page">
                <ExchangeBanner />
                <Hero />

                <DailyBlogPost />

                <section className="home-section">
                    <div className="home-section-header">
                        <span className="home-eyebrow">Bot platform</span>
                        <h2 className="home-section-title">Three ways to automate</h2>
                        <p className="home-section-text">
                            Pick the bot that fits your market view — or run several in parallel.
                        </p>
                    </div>
                    <div className="home-card-grid home-card-grid--3">
                        {BOT_TYPES.map((bot) => (
                            <Link key={bot.title} to={bot.link} className="home-feature-card">
                                <span className="home-feature-icon" aria-hidden="true">
                                    <Icon icon={bot.icon} width={22} height={22} />
                                </span>
                                <h3 className="home-feature-title">{bot.title}</h3>
                                <p className="home-feature-text">{bot.description}</p>
                                <span className="home-feature-link">Setup guide →</span>
                            </Link>
                        ))}
                    </div>
                </section>

                <section className="home-section">
                    <div className="home-section-header">
                        <span className="home-eyebrow">Platform</span>
                        <h2 className="home-section-title">Built for serious traders</h2>
                        <p className="home-section-text">
                            Everything you need to go from idea to live bot — without the clutter.
                        </p>
                    </div>
                    <div className="home-card-grid">
                        {PLATFORM_FEATURES.map((item) => (
                            <FeatureCard key={item.title} item={item} />
                        ))}
                    </div>
                </section>

                <WhyReasons />

                <section className="home-cta-wrap">
                    <div className="home-cta-card">
                        <h2 className="home-cta-title">Start automating today</h2>
                        <p className="home-cta-text">
                            Create a free account, read today&apos;s trading tip, and launch your first bot
                            with our step-by-step guides.
                        </p>
                        <AppButtonLink to="/register" variant="secondary" size="lg">
                            Create free account
                        </AppButtonLink>
                    </div>
                </section>
            </div>
        </Layout>
    );
};

export default Home;